<template>
  <div class="card" :class="{ 'opacity-40': !deviceStore.isConnected }">
    <!-- Header with Load Preset button and Save to Device button on right -->
    <div class="flex items-center justify-between mb-4">
      <h2 class="card-header mb-0">Configuration</h2>
      
      <!-- Right side buttons group -->
      <div class="flex items-center gap-2">
        <!-- Load Presets Button (inline width, justified right) -->
        <div class="relative" ref="presetDropdown">
          <button
            @click="togglePresetDropdown"
            :disabled="loading"
            class="btn-outline-primary flex items-center gap-2 text-sm px-4 py-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            <span>Load Preset</span>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
              <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
            </svg>
          </button>
        
          <!-- Dropdown Menu -->
          <div
            v-show="showPresetDropdown"
            class="dropdown-menu max-h-80 overflow-y-auto min-w-[300px]"
          >
            <button
              v-for="preset in configStore.presets"
              :key="preset.filename"
              @click="selectPreset(preset.filename)"
              class="dropdown-item"
              :class="{ 'active': configStore.selectedPreset === preset.filename }"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-gray-400">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
              </svg>
              <div class="flex-1">
                <div class="font-medium">{{ preset.name }}</div>
                <div class="text-xs text-gray-400">{{ preset.filename }}</div>
              </div>
            </button>
          </div>
        </div>
        
        <!-- Save to Device Split Button (was Read from Device) -->
        <div class="relative" ref="actionDropdown">
          <div class="flex">
            <!-- Primary Action Button -->
            <button
              @click="handleWriteToDevice"
              :disabled="!isDeviceReady || loading || !isValidConfig || !hasConfig"
              class="btn-primary flex items-center justify-center gap-2 text-sm px-4 py-2 rounded-r-none"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
              </svg>
              <span>{{ loading && loadingAction === 'write' ? 'Saving...' : 'Save to Device' }}</span>
            </button>
            
            <!-- Dropdown Toggle -->
            <button
              @click="toggleActionDropdown"
              :disabled="loading"
              class="btn-primary rounded-l-none border-l-2 border-blue-600 px-3 py-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
                <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
              </svg>
            </button>
          </div>
          
          <!-- Dropdown Menu -->
          <div
            v-show="showActionDropdown"
            class="dropdown-menu min-w-[200px] w-full"
          >
            <button
              @click="handleReadFromDevice"
              :disabled="!isDeviceReady || loading"
              class="dropdown-item"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              <span>{{ loading && loadingAction === 'read' ? 'Reading...' : 'Read from Device' }}</span>
            </button>
            
            <button
              @click="handleSaveToFile"
              :disabled="loading || !hasConfig"
              class="dropdown-item"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25M9 16.5v.75m3-3v3M15 12v5.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
              </svg>
              <span>Save to File</span>
            </button>
            
            <button
              @click="triggerFileLoad"
              :disabled="loading"
              class="dropdown-item"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
              </svg>
              <span>Load from File</span>
            </button>
          </div>
          
          <input
            type="file"
            ref="fileInput"
            @change="handleLoadFromFile"
            accept=".cfg"
            style="display: none"
          />
        </div>
      </div>
    </div>
    
    <div v-if="configStore.selectedPreset" class="mb-4 text-sm text-center text-gray-500">
      Current: <span class="font-semibold">{{ configStore.configName }}</span>
    </div>
    
    <!-- Configuration Form with Sliders -->
    <div class="space-y-5 mb-6">
      <!-- Roll Warning Angle -->
      <div>
        <label class="label-normal">Roll Warning Angle</label>
        <div class="flex items-center gap-3">
          <input
            type="range"
            v-model.number="rollWarning"
            min="0"
            max="90"
            step="1"
            @input="handleValueChange"
            class="slider slider-half"
            :disabled="loading || !hasConfig"
          />
          <input
            type="number"
            v-model.number="rollWarning"
            min="0"
            max="90"
            step="1"
            @input="handleValueChange"
            class="input-normal text-center"
            :disabled="loading || !hasConfig"
          />
          <span class="text-base font-semibold text-gray-300">°</span>
        </div>
        <span class="text-xs text-gray-500 mt-1 block">Range: 0-90°, step 1°</span>
      </div>
      
      <!-- Pitch Warning Angle -->
      <div>
        <label class="label-normal">Pitch Warning Angle</label>
        <div class="flex items-center gap-3">
          <input
            type="range"
            v-model.number="pitchWarning"
            min="0"
            max="90"
            step="1"
            @input="handleValueChange"
            class="slider slider-half"
            :disabled="loading || !hasConfig"
          />
          <input
            type="number"
            v-model.number="pitchWarning"
            min="0"
            max="90"
            step="1"
            @input="handleValueChange"
            class="input-normal text-center"
            :disabled="loading || !hasConfig"
          />
          <span class="text-base font-semibold text-gray-300">°</span>
        </div>
        <span class="text-xs text-gray-500 mt-1 block">Range: 0-90°, step 1°</span>
      </div>
      
      <!-- Roll Over Alarm Angle -->
      <div>
        <label class="label-normal">Roll Over Alarm Angle</label>
        <div class="flex items-center gap-3">
          <input
            type="range"
            v-model.number="alarmAngle"
            min="0"
            max="180"
            step="1"
            @input="handleValueChange"
            class="slider slider-half"
            :class="{ 'slider-error': validationError }"
            :disabled="loading || !hasConfig"
          />
          <input
            type="number"
            v-model.number="alarmAngle"
            min="0"
            max="180"
            step="1"
            @input="handleValueChange"
            class="input-normal text-center"
            :class="{ 'border-red-500': validationError }"
            :disabled="loading || !hasConfig"
          />
          <span class="text-base font-semibold text-gray-300">°</span>
        </div>
        <span class="text-xs text-gray-500 mt-1 block">Range: 0-180°, step 1°</span>
        <div v-if="validationError" class="text-sm text-red-600 mt-2 font-semibold">
          ⚠ {{ validationError }}
        </div>
      </div>
      
      <!-- Warning Buzzer - Normal Switch -->
      <div>
        <label class="label-normal">Warning Buzzer</label>
        <label class="switch-container-normal">
          <input 
            type="checkbox" 
            v-model="buzzerEnabled"
            @change="handleValueChange"
            :disabled="loading || !hasConfig"
            class="switch-input"
          />
          <span class="switch-slider-normal"></span>
          <span class="switch-label-normal">{{ buzzerEnabled ? 'Enabled' : 'Disabled' }}</span>
        </label>
      </div>
      
      <!-- Forward Axis -->
      <div>
        <label class="label-normal">Forward Axis</label>
        <div class="flex gap-2 mt-2">
          <label class="radio-container-small">
            <input
              type="radio"
              name="orientation"
              value="Y"
              v-model="forwardAxis"
              @change="handleValueChange"
              :disabled="loading || !hasConfig"
              class="radio-input-small"
            />
            <span class="radio-label-small">Y-axis</span>
          </label>
          <label class="radio-container-small">
            <input
              type="radio"
              name="orientation"
              value="X"
              v-model="forwardAxis"
              @change="handleValueChange"
              :disabled="loading || !hasConfig"
              class="radio-input-small"
            />
            <span class="radio-label-small">X-axis</span>
          </label>
          <button
            type="button"
            class="bg-transparent border-0 p-0 text-sm text-blue-400 underline hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded"
            @click="showForwardAxisInfoModal = true"
          >
            More info
          </button>
        </div>
      </div>
    </div>

    <!-- Forward Axis Info Modal -->
    <div
      v-if="showForwardAxisInfoModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      @click.self="showForwardAxisInfoModal = false"
    >
      <div class="w-full max-w-2xl rounded-lg bg-[#13183b] border border-[#1e2654] p-6 text-gray-200 shadow-2xl">
        <h3 class="text-xl font-semibold mb-4">Forward Axis Setting</h3>

        <p class="mb-4">
          The device needs to know which direction is <strong class="text-white">forward</strong> relative to the vehicle's travel direction.
          The default configuration assumes the <strong class="text-white">Y-Axis</strong> is pointing forward.
        </p>

        <p class="mb-3">The correct Y-Axis orientation for each Safetrax device variant is listed below:</p>

        <ul class="list-disc pl-5 space-y-3 mb-4">
          <li>
            <strong class="text-white">In-cab and Compact models:</strong> Install the device with the
            <strong class="text-white">cables pointing towards the rear of the vehicle</strong>.
          </li>
          <li>
            <strong class="text-white">Quad model:</strong> Install the device with the
            <strong class="text-white">cables pointing towards the right side of the vehicle</strong>.
          </li>
        </ul>

        <p class="mb-4">
          If the installation location does not allow these orientations, the device can be rotated
          <strong class="text-white">90°</strong> and configured to use the <strong class="text-white">X-Axis</strong> as the forward axis.
        </p>

        <p class="mb-4 p-3 rounded bg-[#1a1f45] border border-[#2a3060] text-sm">
          <strong class="text-white">Note:</strong> After changing the forward axis setting, and before permanently mounting the device, confirm the orientation is correct.
          Gently roll the device left and right beyond the configured roll threshold and confirm that the
          <strong class="text-white">roll warning</strong> activates.
          Repeat the test for pitch by tilting the device forward and backward.
        </p>

        <p class="text-sm text-gray-400">
          For more information, refer to the installation instructions or contact
          <strong class="text-gray-300">Fleetpin Technical Support</strong> on
          <strong class="text-gray-300">0800 110 820</strong>.
        </p>

        <div class="mt-6 flex justify-end">
          <button
            type="button"
            class="btn-primary"
            @click="showForwardAxisInfoModal = false"
          >
            OK
          </button>
        </div>
      </div>
    </div>
    
    <!-- Status Messages -->
    <div v-if="deviceStore.waitingForWakeup" class="mt-4 p-3 rounded bg-yellow-100 text-yellow-800 text-sm">
      ⏳ Device appears asleep. Press the device button or turn key on. Device commands are paused until wake-up.
    </div>

    <div v-if="message" :class="messageClass" class="mt-4 p-3 rounded text-sm">
      {{ message }}
    </div>

    <!-- Write Validation Result -->
    <div
      v-if="writeValidationSummary"
      class="mt-4 p-3 rounded text-sm"
      :class="writeValidationSummary.valid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
    >
      <div class="font-semibold">
        {{ writeValidationSummary.valid ? '✓ Write audit passed' : '✗ Write audit failed' }}
      </div>
      <div class="mt-1">
        {{ writeValidationSummary.detail }}
      </div>
    </div>
    
    <!-- Dirty State Warning -->
    <div v-if="isDirty && hasConfig" class="mt-4 p-3 rounded bg-yellow-100 text-yellow-800 text-sm">
      ⚠ Unsaved changes - configuration has been modified
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, onBeforeUnmount } from 'vue';
import { useConfigStore } from '@/stores/config';
import { useDeviceStore } from '@/stores/device';
import { serializeConfigFile } from '@/lib/utils';

// Stores
const configStore = useConfigStore();
const deviceStore = useDeviceStore();

// Form state
const rollWarning = ref(0);
const pitchWarning = ref(0);
const alarmAngle = ref(0);
const buzzerEnabled = ref(true);
const forwardAxis = ref('Y');

// UI state
const selectedPreset = ref('');
const loading = ref(false);
const loadingAction = ref('');
const message = ref('');
const messageType = ref('info');
const fileInput = ref(null);
const showPresetDropdown = ref(false);
const showActionDropdown = ref(false);
const showForwardAxisInfoModal = ref(false);
const presetDropdown = ref(null);
const actionDropdown = ref(null);

// Computed properties
const hasConfig = computed(() => configStore.hasConfig);
const isDeviceReady = computed(() => deviceStore.isConnected && !deviceStore.waitingForWakeup);

const validationError = computed(() => {
  if (!hasConfig.value) return null;
  
  const maxWarning = Math.max(rollWarning.value, pitchWarning.value);
  if (alarmAngle.value < maxWarning) {
    return `Alarm angle must be >= ${maxWarning}° (max of warning angles)`;
  }
  return null;
});

const isValidConfig = computed(() => {
  if (!hasConfig.value) return false;
  
  // Check ranges
  if (rollWarning.value < 0 || rollWarning.value > 90) return false;
  if (pitchWarning.value < 0 || pitchWarning.value > 90) return false;
  if (alarmAngle.value < 0 || alarmAngle.value > 180) return false;
  
  // Check alarm angle constraint
  if (validationError.value) return false;
  
  return true;
});

const isDirty = computed(() => {
  if (!configStore.parsedConfig) return false;
  
  return (
    rollWarning.value !== configStore.parsedConfig.rollWarning ||
    pitchWarning.value !== configStore.parsedConfig.pitchWarning ||
    alarmAngle.value !== configStore.parsedConfig.alarmAngle ||
    buzzerEnabled.value !== configStore.parsedConfig.buzzerEnabled ||
    (forwardAxis.value === 'X') !== configStore.parsedConfig.xAxisForward
  );
});

const messageClass = computed(() => {
  const classes = {
    info: 'bg-blue-100 text-blue-800',
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800'
  };
  return classes[messageType.value] || classes.info;
});

const writeValidationSummary = computed(() => {
  const validation = configStore.lastWriteValidation;
  if (!validation) return null;

  if (validation.valid) {
    return {
      valid: true,
      detail: `Readback matched ${validation.actualLength} bytes from device with valid checksum.`
    };
  }

  const firstMismatch = validation.mismatches?.[0];
  const mismatchText = firstMismatch
    ? `First mismatch at byte ${firstMismatch.index} (sent ${firstMismatch.expected}, read ${firstMismatch.actual}).`
    : 'No explicit byte mismatch details available.';

  return {
    valid: false,
    detail: `${validation.mismatchCount} mismatch(es). ${mismatchText}`
  };
});

// Methods
function togglePresetDropdown() {
  showPresetDropdown.value = !showPresetDropdown.value;
  showActionDropdown.value = false;
}

function toggleActionDropdown() {
  showActionDropdown.value = !showActionDropdown.value;
  showPresetDropdown.value = false;
}

function closeDropdowns(event) {
  if (presetDropdown.value && !presetDropdown.value.contains(event.target)) {
    showPresetDropdown.value = false;
  }
  if (actionDropdown.value && !actionDropdown.value.contains(event.target)) {
    showActionDropdown.value = false;
  }
}

async function selectPreset(preset) {
  selectedPreset.value = preset;
  showPresetDropdown.value = false;
  await handleLoadPreset();
}

function showMessage(type, msg) {
  messageType.value = type;
  message.value = msg;
  
  if (type === 'success' || type === 'info') {
    setTimeout(() => {
      message.value = '';
    }, 5000);
  }
}

function updateFormFromStore() {
  if (configStore.parsedConfig) {
    rollWarning.value = configStore.parsedConfig.rollWarning;
    pitchWarning.value = configStore.parsedConfig.pitchWarning;
    alarmAngle.value = configStore.parsedConfig.alarmAngle;
    buzzerEnabled.value = configStore.parsedConfig.buzzerEnabled;
    forwardAxis.value = configStore.parsedConfig.xAxisForward ? 'X' : 'Y';
  }
}

function handleValueChange() {
  // Apply changes to store
  if (!hasConfig.value) return;
  
  try {
    configStore.updateValues({
      rollWarning: rollWarning.value,
      pitchWarning: pitchWarning.value,
      alarmAngle: alarmAngle.value,
      buzzerEnabled: buzzerEnabled.value,
      xAxisForward: forwardAxis.value === 'X'
    });
  } catch (err) {
    showMessage('error', `Failed to update: ${err.message}`);
  }
}

async function handleLoadPreset() {
  if (!selectedPreset.value) return;
  
  loading.value = true;
  loadingAction.value = 'preset';
  message.value = '';
  
  try {
    await configStore.loadPreset(selectedPreset.value);
    updateFormFromStore();
    showMessage('success', `Loaded preset: ${configStore.configName}`);
  } catch (err) {
    showMessage('error', `Failed to load preset: ${err.message}`);
    selectedPreset.value = '';
  } finally {
    loading.value = false;
    loadingAction.value = '';
  }
}

async function handleWriteToDevice() {
  showActionDropdown.value = false;
  
  if (!isValidConfig.value) {
    showMessage('error', 'Cannot save invalid configuration');
    return;
  }
  
  loading.value = true;
  loadingAction.value = 'write';
  message.value = '';
  
  try {
    await configStore.writeToDevice();
    if (configStore.lastWriteValidation?.valid) {
      showMessage('success', 'Configuration saved and validated by device readback audit');
    } else {
      showMessage('warning', 'Configuration saved, but validation status is uncertain');
    }
  } catch (err) {
    showMessage('error', `Failed to save to device: ${err.message}`);
  } finally {
    loading.value = false;
    loadingAction.value = '';
  }
}

async function handleReadFromDevice() {
  showActionDropdown.value = false;
  loading.value = true;
  loadingAction.value = 'read';
  message.value = '';
  
  try {
    await configStore.readFromDevice();
    updateFormFromStore();
    selectedPreset.value = '';
    showMessage('success', 'Configuration read from device');
  } catch (err) {
    showMessage('error', `Failed to read from device: ${err.message}`);
  } finally {
    loading.value = false;
    loadingAction.value = '';
  }
}

function handleSaveToFile() {
  showActionDropdown.value = false;
  
  if (!configStore.configBytes) {
    showMessage('error', 'No configuration to save');
    return;
  }
  
  try {
    // Generate filename from config name and timestamp
    const timestamp = new Date().toISOString().slice(0, 10);
    const baseName = configStore.configName.replace(/[^a-zA-Z0-9-]/g, '_');
    const filename = `${baseName}_${timestamp}.cfg`;
    
    // Serialize config to text format
    const content = serializeConfigFile(configStore.configName, configStore.configBytes);
    
    // Create blob and download
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    showMessage('success', `Configuration saved to ${filename}`);
  } catch (err) {
    showMessage('error', `Failed to save file: ${err.message}`);
  }
}

function triggerFileLoad() {
  showActionDropdown.value = false;
  if (fileInput.value) {
    fileInput.value.click();
  }
}

async function handleLoadFromFile(event) {
  const file = event.target.files?.[0];
  if (!file) return;
  
  loading.value = true;
  loadingAction.value = 'file';
  message.value = '';
  
  try {
    const text = await file.text();
    
    // Import using config manager
    const { parseConfigFile } = await import('@/lib/utils');
    const { name, bytes, config } = parseConfigFile(text);
    
    // Update store
    configStore.configBytes = bytes;
    configStore.parsedConfig = config;
    configStore.configName = name;
    configStore.selectedPreset = null;
    configStore.isDirty = false;
    configStore.lastLoaded = new Date();
    
    // Update form
    updateFormFromStore();
    selectedPreset.value = '';
    
    deviceStore.addLog('success', `Loaded config from file: ${file.name}`);
    showMessage('success', `Configuration loaded from ${file.name}`);
  } catch (err) {
    showMessage('error', `Failed to load file: ${err.message}`);
  } finally {
    loading.value = false;
    loadingAction.value = '';
    // Reset file input
    if (fileInput.value) {
      fileInput.value.value = '';
    }
  }
}

// Watch for store changes
watch(() => configStore.parsedConfig, () => {
  updateFormFromStore();
}, { deep: true });

// Initialize
onMounted(async () => {
  try {
    // Load preset list
    await configStore.loadPresets();
  } catch (err) {
    console.error('Failed to load presets:', err);
  }
  
  // Initialize form if config already loaded
  if (configStore.parsedConfig) {
    updateFormFromStore();
  }
  
  // Add click outside listener
  document.addEventListener('click', closeDropdowns);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', closeDropdowns);
});
</script>

<style scoped>
/* Normal Labels - Brighter */
.label-normal {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #e5e7eb;
  margin-bottom: 0.5rem;
}

/* Normal Number Inputs */
.input-normal {
  width: 5rem;
  padding: 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  border: 2px solid #d1d5db;
  border-radius: 0.375rem;
  background-color: white;
  color: #1f2937;
}

.input-normal:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.input-normal:disabled {
  background-color: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
}

/* Sliders */
.slider {
  -webkit-appearance: none;
  appearance: none;
  height: 10px;
  border-radius: 5px;
  background: #e5e7eb;
  outline: none;
  transition: background 0.2s;
}

.slider-half {
  width: 50%;
  flex-shrink: 0;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.2s;
}

.slider::-webkit-slider-thumb:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.4);
}

.slider::-moz-range-thumb {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.2s;
}

.slider::-moz-range-thumb:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.4);
}

.slider:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.slider-error::-webkit-slider-thumb {
  background: #ef4444;
}

.slider-error::-moz-range-thumb {
  background: #ef4444;
}

/* Normal Switch */
.switch-container-normal {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  user-select: none;
  padding: 0.25rem 0;
}

.switch-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.switch-slider-normal {
  position: relative;
  display: inline-block;
  width: 52px;
  height: 28px;
  background-color: #d1d5db;
  border-radius: 28px;
  transition: background-color 0.3s;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.switch-slider-normal::before {
  content: '';
  position: absolute;
  width: 22px;
  height: 22px;
  left: 3px;
  top: 3px;
  background-color: white;
  border-radius: 50%;
  transition: transform 0.3s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.switch-input:checked + .switch-slider-normal {
  background-color: #22c55e;
}

.switch-input:checked + .switch-slider-normal::before {
  transform: translateX(24px);
}

.switch-input:disabled + .switch-slider-normal {
  opacity: 0.5;
  cursor: not-allowed;
}

.switch-label-normal {
  font-size: 0.875rem;
  font-weight: 600;
  color: #e5e7eb;
}

/* Normal Radio Buttons */
.radio-container-normal {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
  padding: 0.5rem 0.875rem;
  border: 2px solid #d1d5db;
  border-radius: 0.5rem;
  transition: all 0.2s;
  background-color: white;
}

.radio-container-normal:hover {
  border-color: #3b82f6;
  background-color: #eff6ff;
}

.radio-input-normal {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #3b82f6;
}

.radio-label-normal {
  font-size: 0.875rem;
  font-weight: 500;
  color: #1f2937;
}

/* Small Radio Buttons (dark theme compatible) */
.radio-container-small {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
  padding: 0.75rem 1rem;
  border: 2px solid #4b5563;
  border-radius: 0.5rem;
  transition: all 0.2s;
  background-color: #1f2937;
}

.radio-container-small:hover {
  border-color: #3b82f6;
  background-color: #374151;
}

.radio-input-small {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #3b82f6;
  flex-shrink: 0;
}

.radio-label-small {
  font-size: 0.875rem;
  font-weight: 500;
  color: #e5e7eb;
}

/* Dropdown Menu */
.dropdown-menu {
  position: absolute;
  z-index: 50;
  margin-top: 0.5rem;
  right: 0;
  background-color: #1a1f45;
  border: 1px solid #2a3060;
  border-radius: 0.5rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  overflow: hidden;
}

.dropdown-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.875rem;
  font-weight: 500;
  color: #e5e7eb;
  background-color: transparent;
  border-bottom: 1px solid #2a3060;
  transition: background-color 0.15s;
  cursor: pointer;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover:not(:disabled) {
  background-color: rgba(59, 130, 246, 0.15);
}

.dropdown-item.active {
  background-color: rgba(59, 130, 246, 0.2);
}

.dropdown-item:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Custom scrollbar for dark dropdown */
.dropdown-menu::-webkit-scrollbar {
  width: 6px;
}

.dropdown-menu::-webkit-scrollbar-track {
  background: transparent;
}

.dropdown-menu::-webkit-scrollbar-thumb {
  background: #3a4070;
  border-radius: 3px;
}

.dropdown-menu::-webkit-scrollbar-thumb:hover {
  background: #4a5090;
}

.input-normal.border-red-500 {
  border-color: #ef4444;
}

.input-normal.border-red-500:focus {
  outline-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

/* Outline primary button (blue outline, no fill) */
.btn-outline-primary {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #3b82f6;
  background-color: transparent;
  border: 2px solid #3b82f6;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-outline-primary:hover:not(:disabled) {
  background-color: rgba(59, 130, 246, 0.1);
  border-color: #2563eb;
  color: #2563eb;
}

.btn-outline-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
